<script setup lang="ts">
import type { TBankItemCard } from './types'
import { ChevronDown } from 'lucide-vue-next'
import { bankListItemLinks } from '~/data/pages/banks/mocks'
import { TransitionBlock } from '~/components/ui/transition-block'
import { Rating } from '~/components/ui/rating'
import { UiCardTop } from '~/components/ui/card'
import { EntityOrganizationLogo } from '~/components/entity/organization-logo'

const props = defineProps<{ item: TBankItemCard }>()
const isOpen = ref(false)
const toggle = () => (isOpen.value = !isOpen.value)

const filteredBankListItemLinks = bankListItemLinks.filter((link) => {
  const fieldValue = props.item[link.type as keyof TBankItemCard]
  return (
    (fieldValue !== undefined && fieldValue !== null && fieldValue !== 0) ||
    link.type === 'news_count' ||
    link.type === 'rates'
  )
})

const hasDetails = computed(() =>
  [props.item.branches_count, props.item.atms_count, props.item.terminals_count].some((el) => el)
)

// The title may carry the search highlight (<em>); attributes need plain text
const plainTitle = computed(() => useStripTags(props.item.title))
</script>

<template>
  <article :class="[props.item.highlight ? 'CardPremium group' : 'Card']">
    <UiCardTop v-if="props.item.highlight" />
    <div class="flex flex-col md:flex-row md:justify-between">
      <div class="relative flex flex-col md:static md:flex-row">
        <div class="absolute left-0 top-0 pr-4 md:static md:left-auto md:top-auto">
          <EntityOrganizationLogo
            :src="props.item.logo"
            :alt="plainTitle"
            :to="props.item.url"
            :aria-label="`${$t('common.more_about')} ${plainTitle}`"
            :highlighted="!!props.item.highlight"
          />
        </div>
        <div class="flex flex-col justify-between gap-0.5">
          <div class="flex flex-col gap-0.5 pl-16 md:pl-0">
            <NuxtLinkLocale
              :to="props.item.url"
              :aria-label="`${$t('common.more_about')} ${plainTitle}`"
              class="inline-block hover:underline"
            >
              <Heading tag="h3" class="hit-title h6"><span v-hydrated-html="props.item.title" /></Heading>
            </NuxtLinkLocale>
            <div v-if="props.item.ratings" class="flex items-center">
              <Rating :value="Number(props.item.ratings)" size="xs" />
              <div class="ml-2 text-sm text-gray-900">{{ props.item.ratings }}</div>
            </div>
          </div>
          <div
            :class="props.item.ratings ? 'mt-4' : 'mt-10'"
            class="flex flex-wrap gap-3 font-semibold md:mt-0 md:gap-6"
          >
            <NuxtLinkLocale
              v-for="(link, i) in filteredBankListItemLinks"
              :key="i"
              :to="`${props.item.url}${link.url}`"
              class="CardNavLink"
            >
              {{ $t(link.name) }}
            </NuxtLinkLocale>
          </div>
        </div>
      </div>
      <div class="mt-4 flex items-center gap-2 md:mt-0">
        <div class="flex w-full items-center justify-between md:w-auto md:justify-end">
          <Button v-if="props.item.highlight" as-child>
            <NuxtLinkLocale
              :to="props.item.url"
              :aria-label="`${$t('common.more_about')} ${plainTitle}`"
              >{{ $t('common.more_details') }}</NuxtLinkLocale
            >
          </Button>
        </div>
        <div
          v-if="hasDetails"
          class="flex cursor-pointer items-center gap-2 text-sm font-semibold text-gray-500"
          role="button"
          tabindex="0"
          :aria-expanded="isOpen"
          :aria-label="$t('entity.bank.item.more')"
          @click="toggle"
          @keydown.enter.prevent="toggle"
          @keydown.space.prevent="toggle"
        >
          <span class="md:hidden">{{ $t('entity.bank.item.more') }}</span>
          <ChevronDown
            class="h-5 w-5 text-gray-500 transition-transform duration-100"
            :class="{ 'rotate-180': isOpen }"
          />
        </div>

        <div v-else class="min-w-5" />
      </div>
    </div>
    <TransitionBlock>
      <div
        v-if="isOpen"
        class="mt-4 flex flex-wrap justify-between gap-4 border-t pt-4 md:grid md:grid-cols-5 md:gap-4"
      >
        <div v-if="props.item.branches_count" class="flex flex-col md:w-auto">
          <span class="text-sm leading-4 text-gray-700">{{
            $t('entity.bank.item.branches_and_departments')
          }}</span>
          <NuxtLinkLocale :to="`${props.item.url}/branches`" class="CardNavLinkSecondary">
            {{ props.item.branches_count }}
          </NuxtLinkLocale>
        </div>
        <div v-if="props.item.atms_count" class="flex flex-col md:w-auto">
          <span class="text-sm leading-4 text-gray-700">{{ $t('common.atms') }}</span>
          <NuxtLinkLocale :to="`${props.item.url}/atms`" class="CardNavLinkSecondary">
            {{ props.item.atms_count }}
          </NuxtLinkLocale>
        </div>
        <div v-if="props.item.terminals_count" class="flex flex-col md:w-auto">
          <span class="text-sm leading-4 text-gray-700">{{
            $t('entity.bank.item.terminals')
          }}</span>
          <NuxtLinkLocale :to="`${props.item.url}/terminals`" class="CardNavLinkSecondary">
            {{ props.item.terminals_count }}
          </NuxtLinkLocale>
        </div>
      </div>
    </TransitionBlock>
  </article>
</template>
